import React, { useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../../services/api";
import {
  PlusCircle,
  Edit2,
  Trash2,
  Search,
  X,
  Check,
  Loader2,
  MapPin,
  ChevronRight,
  Filter
} from "lucide-react";

const API = `${API_BASE_URL}/api/source`;

const Sources = () => {
  const [sources, setSources] = useState([]);
  const [name, setName] = useState("");
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchSources = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(API);
      setSources(res.data.data);
    } catch (err) {
      setError("Failed to fetch sources. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Source name cannot be empty");
      return;
    }

    setSubmitLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (editId) {
        await axios.put(`${API}/${editId}`, { name });
        setSuccess("Source updated successfully!");
      } else {
        await axios.post(API, { name });
        setSuccess("Source added successfully!");
      }

      setName("");
      setEditId(null);
      fetchSources();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "An error occurred. Please try again.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (source) => {
    setName(source.name);
    setEditId(source.id);
    const formElement = document.getElementById("source-form");
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this source?")) return;
    
    try {
      await axios.delete(`${API}/${id}`);
      setSuccess("Source deleted successfully!");
      fetchSources();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError("Failed to delete source. Please try again.");
    }
  };

  const handleCancel = () => {
    setName("");
    setEditId(null);
    setError(null);
  };

  const filteredSources = sources.filter(source =>
    source.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    fetchSources();
  }, []);

  return (
    <div className="min-h-screen ">
      <div >
     

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Column - Form */}
          <div className="lg:col-span-1" id="source-form">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
              <div className="flex items-center gap-2 mb-5">
                <div className="p-1.5 bg-blue-50 rounded-md">
                  <MapPin className="w-4 h-4 text-blue-600" />
                </div>
                <h2 className="text-base font-semibold text-gray-800">
                  {editId ? "Edit Source" : "Add Source"}
                </h2>
              </div>
              
              {/* Messages */}
              {success && (
                <div className="mb-4 p-2.5 bg-green-50 border border-green-100 rounded-md flex items-center gap-2 text-xs">
                  <Check className="w-3.5 h-3.5 text-green-600" />
                  <span className="text-green-700">{success}</span>
                </div>
              )}

              {error && (
                <div className="mb-4 p-2.5 bg-red-50 border border-red-100 rounded-md flex items-center gap-2 text-xs">
                  <X className="w-3.5 h-3.5 text-red-600" />
                  <span className="text-red-700">{error}</span>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Source Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter source name"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      setError(null);
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 outline-none"
                    disabled={submitLoading}
                  />
                </div>

                <div className="flex gap-2 pt-1">
                  <button
                    type="submit"
                    disabled={submitLoading || !name.trim()}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 text-sm font-medium rounded-md hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                  >
                    {submitLoading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : editId ? (
                      <>
                        <Edit2 className="w-3.5 h-3.5" />
                        Update
                      </>
                    ) : (
                      <>
                        <PlusCircle className="w-3.5 h-3.5" />
                        Add Source
                      </>
                    )}
                  </button>
                  
                  {editId && (
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-200"
                      disabled={submitLoading}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>

              {/* Stats */}
              <div className="mt-6 pt-5 border-t border-gray-100">
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
                  Overview
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-gray-50 p-3 rounded-md border border-gray-100">
                    <div className="text-lg font-semibold text-gray-800">{sources.length}</div>
                    <div className="text-xs text-gray-500 mt-0.5">Total Sources</div>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-md border border-blue-100">
                    <div className="text-lg font-semibold text-blue-700">{filteredSources.length}</div>
                    <div className="text-xs text-blue-600 mt-0.5">Displayed</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Sources List */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              {/* Table Header */}
              <div className="p-5 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <div>
                    <h2 className="text-base font-semibold text-gray-800">Sources</h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {filteredSources.length} of {sources.length} sources
                    </p>
                  </div>
                  
                  <div className="relative w-full sm:w-56">
                    <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search sources..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 outline-none"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm("")}
                        className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Table Content */}
              <div className="overflow-hidden">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-blue-600 animate-spin mb-2" />
                    <p className="text-sm text-gray-600">Loading sources...</p>
                  </div>
                ) : filteredSources.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <Filter className="w-5 h-5 text-gray-400" />
                    </div>
                    <h3 className="text-sm font-medium text-gray-700 mb-1">
                      {searchTerm ? "No sources found" : "No sources yet"}
                    </h3>
                    <p className="text-xs text-gray-500 max-w-xs mx-auto">
                      {searchTerm
                        ? "No sources match your search criteria"
                        : "Get started by adding your first source"}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="py-3 px-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-200">
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5" />
                              Source Name
                            </div>
                          </th>
                          <th className="py-3 px-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-200 w-28">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredSources.map((source) => (
                          <tr 
                            key={source.id} 
                            className="hover:bg-gray-50 transition-colors duration-150"
                          >
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2.5">
                                <div className="flex-shrink-0 w-6 h-6 bg-blue-50 rounded flex items-center justify-center">
                                  <span className="text-xs font-medium text-blue-600">
                                    {source.name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <span className="text-sm text-gray-800 font-medium">
                                  {source.name}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleEdit(source)}
                                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200"
                                  title="Edit"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDelete(source.id)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors duration-200"
                                  title="Delete"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Table Footer */}
              {filteredSources.length > 0 && (
                <div className="px-5 py-3 border-t border-gray-200">
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div>
                      Showing <span className="font-medium">{filteredSources.length}</span> sources
                      {searchTerm && (
                        <span className="ml-2">
                          for "<span className="font-medium">{searchTerm}</span>"
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        <span>Active</span>
                      </div>
                      <div className="text-gray-400">|</div>
                      <div>Last updated: {new Date().toLocaleDateString()}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Help Text */}
            <div className="mt-4 text-xs text-gray-500">
              <p>• Click edit to modify a source name</p>
              <p className="mt-0.5">• Use the search to filter sources quickly</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sources;